import React, { useState } from 'react';
import {
    Box,
    List,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Collapse,
    Button
} from '@mui/material';
import {
    TableChart,
    FeaturedPlayList,
    Today,
    Description,
    Receipt,
    PointOfSale,
    Subject,
    Warehouse,
    MenuBook,
    Monitor,
    Analytics,
    ExpandLess,
    ExpandMore,
    MarkAsUnread,
    Logout,
    ImportContacts,
    QrCode
} from '@mui/icons-material';
import logo from '../logo.png';
import { openOFDTab } from '../ofdUtils';

interface DashboardSidebarProps {
    userRole: string | null;
    selectedIndex: number;
    onMenuItemClick: (index: number) => void;
    onLogout: () => void;
    isInterfaceBlocked: boolean;
}

const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
    userRole,
    selectedIndex,
    onMenuItemClick,
    onLogout,
    isInterfaceBlocked
}) => {
    const [monitoringOpen, setMonitoringOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [documentsOpen, setDocumentsOpen] = useState(
        selectedIndex === 7 || selectedIndex === 12 || userRole === 'storage'
    );

    const handleMenuClick = (index: number) => {
        if (index === 8) {
            openOFDTab();
        } else {
            onMenuItemClick(index);
        }
    };

    const topMenuItems = [
        { index: 0, label: 'Текущий отчет', icon: <TableChart /> },
        { index: 1, label: 'Отчет за месяц', icon: <FeaturedPlayList /> },
        { index: 3, label: 'Табель РБ', icon: <Today /> }
    ];

    const documentItems = [
        { index: 7, label: 'Документооборот', icon: <Description /> },
        { index: 12, label: 'КМ\'ы', icon: <QrCode /> }
    ];

    const bottomMenuItems = [
        { index: 8, label: 'ОФД', icon: <Receipt /> },
        { index: 10, label: 'Электронный журнал', icon: <ImportContacts /> }
    ];

    const monitoringItems = [
        { index: 4, label: 'Касса', icon: <PointOfSale /> },
        { index: 5, label: 'КМ', icon: <Subject /> }
    ];

    const helpItems = [
        { index: 6, label: 'Остатки', icon: <Warehouse /> },
        { index: 9, label: 'Номенклатура', icon: <MenuBook /> }
    ];

    return (
        <Box
            sx={{
                width: '260px',
                flexShrink: 0,
                bgcolor: '#000000',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                borderRight: '1px solid rgba(255, 255, 255, 0.1)',
                zIndex: 10,
                justifyContent: 'space-between'
            }}
        >
            <Box>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    p: 3,
                    mb: 1
                }}>
                    <img src={logo} style={{ maxWidth: '180px', height: 'auto' }} alt="logo" />
                </Box>

                <List component="nav" sx={{ px: 2 }}>
                    {/* Меню до Документов */}
                    {userRole !== 'storage' && topMenuItems.map((item) => (
                        <ListItemButton
                            key={item.index}
                            selected={selectedIndex === item.index}
                            onClick={() => handleMenuClick(item.index)}
                            disabled={isInterfaceBlocked}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                color: selectedIndex === item.index ? '#ffffff' : '#a1a1aa',
                                bgcolor: selectedIndex === item.index ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                    color: '#ffffff',
                                }
                            }}
                        >
                            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }} />
                        </ListItemButton>
                    ))}

                    {/* Документы Group (выпадающий список) */}
                    <ListItemButton
                        onClick={() => setDocumentsOpen(!documentsOpen)}
                        disabled={isInterfaceBlocked}
                        sx={{
                            borderRadius: '8px',
                            mb: 0.5,
                            color: (selectedIndex === 7 || selectedIndex === 12) ? '#ffffff' : '#a1a1aa',
                            bgcolor: (selectedIndex === 7 || selectedIndex === 12) ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.05)',
                                color: '#ffffff'
                            }
                        }}
                    >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                            <Description />
                        </ListItemIcon>
                        <ListItemText primary="Документы" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }} />
                        {documentsOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                    </ListItemButton>
                    <Collapse in={documentsOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {documentItems.map((item) => (
                                <ListItemButton
                                    key={item.index}
                                    selected={selectedIndex === item.index}
                                    onClick={() => onMenuItemClick(item.index)}
                                    disabled={isInterfaceBlocked}
                                    sx={{
                                        pl: 4,
                                        borderRadius: '8px',
                                        mb: 0.5,
                                        color: selectedIndex === item.index ? '#ffffff' : '#a1a1aa',
                                        bgcolor: selectedIndex === item.index ? 'rgba(255, 255, 255, 0.12)' : 'transparent',
                                        '&:hover': {
                                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                                            color: '#ffffff'
                                        }
                                    }}
                                >
                                    <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Collapse>

                    {/* Меню после Документов */}
                    {userRole !== 'storage' && bottomMenuItems.map((item) => (
                        <ListItemButton
                            key={item.index}
                            selected={selectedIndex === item.index}
                            onClick={() => handleMenuClick(item.index)}
                            disabled={isInterfaceBlocked}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                color: selectedIndex === item.index ? '#ffffff' : '#a1a1aa',
                                bgcolor: selectedIndex === item.index ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                    color: '#ffffff',
                                }
                            }}
                        >
                            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }} />
                        </ListItemButton>
                    ))}

                    {/* Monitoring Group */}
                    {userRole !== 'storage' && (
                        <>
                            <ListItemButton
                                onClick={() => setMonitoringOpen(!monitoringOpen)}
                                disabled={isInterfaceBlocked}
                                sx={{
                                    borderRadius: '8px',
                                    mb: 0.5,
                                    color: (selectedIndex === 4 || selectedIndex === 5) ? '#ffffff' : '#a1a1aa',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                                        color: '#ffffff'
                                    }
                                }}
                            >
                                <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                                    <Monitor />
                                </ListItemIcon>
                                <ListItemText primary="Мониторинг" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }} />
                                {monitoringOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                            </ListItemButton>
                            <Collapse in={monitoringOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {monitoringItems.map((item) => (
                                        <ListItemButton
                                            key={item.index}
                                            selected={selectedIndex === item.index}
                                            onClick={() => onMenuItemClick(item.index)}
                                            disabled={isInterfaceBlocked}
                                            sx={{
                                                pl: 4,
                                                borderRadius: '8px',
                                                mb: 0.5,
                                                color: selectedIndex === item.index ? '#ffffff' : '#a1a1aa',
                                                bgcolor: selectedIndex === item.index ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                                '&:hover': {
                                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                                    color: '#ffffff'
                                                }
                                            }}
                                        >
                                            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Collapse>
                        </>
                    )}

                    {/* Help Group */}
                    {userRole !== 'storage' && (
                        <>
                            <ListItemButton
                                onClick={() => setHelpOpen(!helpOpen)}
                                disabled={isInterfaceBlocked}
                                sx={{
                                    borderRadius: '8px',
                                    mb: 0.5,
                                    color: (selectedIndex === 6 || selectedIndex === 9) ? '#ffffff' : '#a1a1aa',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                                        color: '#ffffff'
                                    }
                                }}
                            >
                                <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                                    <Analytics />
                                </ListItemIcon>
                                <ListItemText primary="Справочник" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }} />
                                {helpOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                            </ListItemButton>
                            <Collapse in={helpOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {helpItems.map((item) => (
                                        <ListItemButton
                                            key={item.index}
                                            selected={selectedIndex === item.index}
                                            onClick={() => onMenuItemClick(item.index)}
                                            disabled={isInterfaceBlocked}
                                            sx={{
                                                pl: 4,
                                                borderRadius: '8px',
                                                mb: 0.5,
                                                color: selectedIndex === item.index ? '#ffffff' : '#a1a1aa',
                                                bgcolor: selectedIndex === item.index ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                                                '&:hover': {
                                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                                    color: '#ffffff'
                                                }
                                            }}
                                        >
                                            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                                                {item.icon}
                                            </ListItemIcon>
                                            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                                        </ListItemButton>
                                    ))}
                                </List>
                            </Collapse>
                        </>
                    )}
                </List>
            </Box>

            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                    fullWidth
                    onClick={onLogout}
                    startIcon={<Logout />}
                    sx={{
                        color: '#ef4444',
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        px: 2,
                        '&:hover': {
                            bgcolor: 'rgba(239, 68, 68, 0.1)'
                        }
                    }}
                >
                    Выйти
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    onClick={() => window.open('https://mail.zerdegroup.kz/', '_blank', 'noopener,noreferrer')}
                    startIcon={<MarkAsUnread />}
                    sx={{
                        color: '#a1a1aa',
                        borderColor: '#3f3f46',
                        textTransform: 'none',
                        '&:hover': {
                            color: 'white',
                            borderColor: 'white',
                            bgcolor: 'rgba(255,255,255,0.05)'
                        }
                    }}
                >
                    Почта
                </Button>
                <Button
                    variant="outlined"
                    fullWidth
                    size="small"
                    onClick={() => window.open('https://guide.zerdegroup.kz/', '_blank', 'noopener,noreferrer')}
                    startIcon={<MenuBook />}
                    sx={{
                        color: '#a1a1aa',
                        borderColor: '#3f3f46',
                        textTransform: 'none',
                        '&:hover': {
                            color: 'white',
                            borderColor: 'white',
                            bgcolor: 'rgba(255,255,255,0.05)'
                        }
                    }}
                >
                    База знаний
                </Button>
            </Box>
        </Box>
    );
};

export default DashboardSidebar;
